import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "hiddenField"]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.currentHighlight = -1
    this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  search() {
    clearTimeout(this.timeout)

    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.currentHighlight = -1
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = ""
      }
      return
    }

    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const results = await response.json()
        this.displayResults(results)
      }
    } catch (error) {
      console.error("Search failed:", error)
    }
  }

  displayResults(results) {
    this.currentHighlight = -1
    // Store full result data for later use
    this.currentResults = results

    if (results.length === 0) {
      this.resultsTarget.innerHTML = ""
      return
    }

    const html = results.map((result, index) =>
      `<div class="search-result"
            data-action="click->typeahead#selectResult mouseenter->typeahead#highlightResult"
            data-id="${result.id}"
            data-index="${index}">
        ${result.name}${result.barcode ? ' — (Barcode: ' + result.barcode + ')' : ''}${result.mpn ? ' — (MPN: ' + result.mpn + ')' : ''}
      </div>`
    ).join("")

    this.resultsTarget.innerHTML = html
  }

  highlightResult(event) {
    // Remove highlight from all results
    this.resultsTarget.querySelectorAll('.search-result').forEach(el => {
      el.classList.remove('highlighted')
    })
    // Add highlight to hovered item
    event.currentTarget.classList.add('highlighted')
    // Update current highlight index
    this.currentHighlight = parseInt(event.currentTarget.dataset.index)
  }

  selectResult(event) {
    const selectedText = event.currentTarget.textContent.trim()
    const selectedId = event.currentTarget.dataset.id
    const selectedIndex = parseInt(event.currentTarget.dataset.index)

    this.inputTarget.value = selectedText

    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = selectedId
    }

    this.resultsTarget.innerHTML = ""
    this.currentHighlight = -1

    // Dispatch custom event with full product data
    const selectedProduct = this.currentResults && this.currentResults[selectedIndex]
    if (selectedProduct) {
      const customEvent = new CustomEvent('typeahead:selected', {
        detail: {
          product: selectedProduct,
          input: this.inputTarget,
          hiddenField: this.hasHiddenFieldTarget ? this.hiddenFieldTarget : null
        },
        bubbles: true
      })
      this.element.dispatchEvent(customEvent)
    }
  }

  handleKeydown(event) {
    const results = this.resultsTarget.querySelectorAll('.search-result')

    if (results.length === 0) return

    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.highlightNext(results)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.highlightPrevious(results)
        break
      case 'Enter':
        event.preventDefault()
        if (this.currentHighlight >= 0) {
          results[this.currentHighlight].click()
        }
        break
      case 'Escape':
        this.resultsTarget.innerHTML = ""
        this.currentHighlight = -1
        break
    }
  }

  highlightNext(results) {
    this.currentHighlight = Math.min(this.currentHighlight + 1, results.length - 1)
    this.updateHighlight(results)
  }

  highlightPrevious(results) {
    this.currentHighlight = Math.max(this.currentHighlight - 1, 0)
    this.updateHighlight(results)
  }

  updateHighlight(results) {
    results.forEach((result, index) => {
      result.classList.toggle('highlighted', index === this.currentHighlight)
    })
  }
}
